import { POSITION_SCALE, type Reading, type WantToRead } from '@bookclub/shared';
import type { TFunction } from 'i18next';
import { router } from 'expo-router';
import { Paintbrush, Plus, Search } from 'lucide-react-native';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, Text, View } from 'react-native';
import { useShelf } from '@/api/preferences';
import { useReadings, useWantToRead } from '@/api/readings';
import { bookErrorMessage } from '@/books/errors';
import { formatAuthors } from '@/books/format';
import { openReading } from '@/books/start';
import { formatDate } from '@/clubs/format';
import { readingErrorMessage } from '@/readings/errors';
import { percentLabel, readingLine } from '@/readings/format';
import { arrangeShelves } from '@/readings/shelves';
import { Shelf, type ShelfBook } from '@/components/Bookshelf';
import { FinishedSheet } from '@/components/FinishedSheet';
import { Mascot } from '@/components/Mascot';
import { PopularBooks } from '@/components/PopularBooks';
import { PageTitle } from '@/components/PageTitle';
import { Screen } from '@/components/Screen';
import { ShelfSheet } from '@/components/ShelfSheet';
import { UpdatePageSheet } from '@/components/UpdatePageSheet';
import { UpdatePill } from '@/components/UpdatePill';
import { Button } from '@/components/ui/Button';
import { IconButton } from '@/components/ui/IconButton';
import { Notice } from '@/components/ui/Notice';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { Hint } from '@/components/ui/Section';
import { TextField } from '@/components/ui/TextField';
import { TextLink } from '@/components/ui/TextLink';
import { shelfMaterials, useTheme } from '@/theme';

function fromReading(r: Reading, status: string): ShelfBook {
  const author = formatAuthors(r.edition.authors);
  return {
    key: r.id,
    href: { pathname: '/readings/[id]', params: { id: r.id } },
    title: r.edition.title,
    author,
    cover: r.edition.cover,
    pages: r.edition.pageCount,
    label: [r.edition.title, author, status].filter(Boolean).join(', '),
  };
}

function fromWant(b: WantToRead): ShelfBook {
  const author = formatAuthors(b.edition.authors);
  return {
    key: b.id,
    href: b.edition.workKey ? { pathname: '/books/work/[key]', params: { key: b.edition.workKey } } : { pathname: '/books/edition/[id]', params: { id: b.edition.id } },
    title: b.edition.title,
    author,
    cover: b.edition.cover,
    pages: b.edition.pageCount,
    label: [b.edition.title, author].filter(Boolean).join(', '),
  };
}

const finishedLine = (t: TFunction, r: Reading) => (r.finishedAt ? t('myBooks.finished', { date: formatDate(r.finishedAt.slice(0, 10)) }) : t('reading.finished'));
const stoppedLine = (t: TFunction, r: Reading) => (r.currentPage ? t('myBooks.stoppedAt', { page: r.currentPage }) : t('myBooks.stoppedEarly'));

/**
 * My books is your bookshelf, top to bottom: what you're reading (your place and Update under each), what
 * you want to read, what you've read by year, and what you stopped. Tap a book to open it; its page has
 * Start reading and the rest. The brush changes the shelf: covers or spines, and what it's made of.
 */
export default function MyBooks() {
  const { t } = useTranslation();
  const { colors, fonts, fontSize, shelf: style, space } = useTheme();
  const readings = useReadings();
  const want = useWantToRead();
  const { shelf } = useShelf();
  const material = shelfMaterials[shelf.finish ?? style.finish];
  const { reading, wanted, years, stopped } = useMemo(() => arrangeShelves(readings.data ?? [], want.data ?? []), [readings.data, want.data]);
  // One sheet for whichever book you tap; kept after closing so it can slide away.
  const [updating, setUpdating] = useState<{ reading: Reading; open: boolean } | null>(null);
  const [finished, setFinished] = useState<Reading | null>(null);
  const [customizing, setCustomizing] = useState(false);
  const loading = readings.isPending || want.isPending;
  const nothing = !loading && !readings.isError && !want.isError && reading.length + wanted.length + years.length + stopped.length === 0;
  // Playful's bunny sits on the first shelf below Reading now that has books.
  const bunnyOn = wanted.length > 0 ? 'want' : years.length > 0 ? 'read' : 'stopped';

  return (
    <Screen width="wide">
      <PageTitle title={t('myBooks.title')} />
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.xs, marginBottom: space.xl }}>
        <Text accessibilityRole="header" style={{ flex: 1, fontFamily: fonts.headingBold, fontSize: fontSize.xxl, lineHeight: fontSize.xxl * 1.15, color: colors.text }}>
          {t('myBooks.title')}
        </Text>
        <IconButton icon={Paintbrush} label={t('shelf.customize')} onPress={() => setCustomizing(true)} />
        <IconButton icon={Plus} label={t('myBooks.addShort')} showLabel tone="accent" onPress={() => router.push({ pathname: '/books', params: { pick: 'read' } })} />
      </View>

      {loading ? (
        <ActivityIndicator color={colors.accent} />
      ) : nothing ? (
        <WhatAreYouReading />
      ) : (
        <View style={{ gap: space.xxl }}>
          {readings.isError && <Notice message={readingErrorMessage(t, readings.error)} />}
          {want.isError && <Notice message={bookErrorMessage(t, want.error)} />}
          <Shelf
            title={t('myBooks.readingNow')}
            large
            material={material}
            books={reading.map((r) => fromReading(r, readingLine(t, r)))}
            caption={(key) => {
              const r = reading.find((x) => x.id === key);
              return r ? <ReadingCaption reading={r} onUpdate={() => setUpdating({ reading: r, open: true })} /> : null;
            }}
            empty={
              <>
                <Hint>{t('myBooks.readingEmpty')}</Hint>
                <View style={{ alignSelf: 'flex-start' }}>
                  <TextLink href={{ pathname: '/books', params: { pick: 'read' } }} label={t('myBooks.find')} />
                </View>
              </>
            }
          />
          <Shelf
            title={t('myBooks.want')}
            layout={shelf.layout}
            material={material}
            bookend={bunnyOn === 'want'}
            books={wanted.map(fromWant)}
            empty={
              <>
                <Hint>{t('myBooks.wantEmpty')}</Hint>
                <View style={{ alignSelf: 'flex-start' }}>
                  <TextLink href="/books" label={t('myBooks.find')} />
                </View>
              </>
            }
          />
          {years.length === 0 && <Shelf title={t('myBooks.readUndated')} material={material} books={[]} empty={<Hint>{t('myBooks.readEmpty')}</Hint>} />}
          {years.map(([year, books], i) => (
            <Shelf
              key={year ?? 'undated'}
              title={year === null ? t('myBooks.readUndated') : t('myBooks.readInYear', { year })}
              layout={shelf.layout}
              material={material}
              bookend={bunnyOn === 'read' && i === 0}
              books={books.map((r) => fromReading(r, finishedLine(t, r)))}
            />
          ))}
          {stopped.length > 0 && (
            <Shelf title={t('myBooks.didntFinish')} layout={shelf.layout} material={material} bookend={bunnyOn === 'stopped'} books={stopped.map((r) => fromReading(r, stoppedLine(t, r)))} />
          )}
        </View>
      )}

      <ShelfSheet visible={customizing} onClose={() => setCustomizing(false)} />
      {updating && (
        <UpdatePageSheet
          key={updating.reading.id}
          reading={updating.reading}
          bookTitle={updating.reading.edition.title}
          visible={updating.open}
          onClose={() => setUpdating((u) => u && { ...u, open: false })}
          onFinished={() => setFinished(updating.reading)}
        />
      )}
      {finished && (
        <FinishedSheet
          key={finished.id}
          reading={finished}
          visible
          onClose={() => setFinished(null)}
          onLastThought={() => {
            setFinished(null);
            openReading(finished.id, 'note');
          }}
          onReadNotes={() => {
            setFinished(null);
            openReading(finished.id);
          }}
        />
      )}
    </Screen>
  );
}

/** Under a book you're reading, below the board: how far, and one tap to update it. */
function ReadingCaption({ reading, onUpdate }: { reading: Reading; onUpdate: () => void }) {
  const { t } = useTranslation();
  const { colors, fontSize, space } = useTheme();
  const percent = percentLabel(reading.position);
  const where = reading.currentPage ? t('myBooks.atPage', { page: reading.currentPage, percent }) : reading.position > 0 ? t('myBooks.atPercent', { percent }) : t('reading.notStarted');
  return (
    <View style={{ gap: space.sm }}>
      <ProgressBar value={reading.position / POSITION_SCALE} />
      <Text numberOfLines={1} style={{ color: colors.textMuted, fontSize: fontSize.xs, fontVariant: ['tabular-nums'] }}>
        {where}
      </Text>
      <UpdatePill title={reading.edition.title} onPress={onUpdate} />
    </View>
  );
}

/** Nothing on the go: the one question that gets you going. */
function WhatAreYouReading() {
  const { t } = useTranslation();
  const { colors, fonts, fontSize, space } = useTheme();
  const [q, setQ] = useState('');
  const go = () => router.push({ pathname: '/books', params: { pick: 'read', ...(q.trim() ? { q: q.trim() } : {}) } });
  return (
    <View style={{ gap: space.lg, paddingTop: space.md }}>
      <Mascot pose="reading" />
      <Text accessibilityRole="header" aria-level={2} style={{ fontFamily: fonts.headingBold, fontSize: fontSize.xl, lineHeight: fontSize.xl * 1.2, color: colors.text }}>
        {t('reading.empty.title')}
      </Text>
      <Text style={{ color: colors.textMuted, fontSize: fontSize.md, lineHeight: fontSize.md * 1.5 }}>{t('reading.empty.body')}</Text>
      <TextField label={t('books.searchLabel')} value={q} onChangeText={setQ} inputMode="search" returnKeyType="search" onSubmitEditing={go} autoCorrect={false} />
      <Button label={t('reading.empty.find')} icon={<Search size={18} color={colors.onAccent} />} onPress={go} />
      <View style={{ alignItems: 'center', marginTop: space.sm }}>
        <TextLink href="/clubs/join" label={t('reading.empty.invite')} />
      </View>
      <PopularBooks title={t('popular.orStart')} />
    </View>
  );
}
